import { useEffect, useRef } from 'react';

type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
      size?: 'normal' | 'flexible' | 'compact' | 'invisible';
    },
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type Props = {
  readonly siteKey: string;
  readonly onToken: (token: string) => void;
  readonly onExpired?: () => void;
  readonly onError?: () => void;
};

export function Turnstile({ siteKey, onToken, onExpired, onError }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    let cancelled = false;
    const startedAt = Date.now();
    const TIMEOUT_MS = 10_000;

    const tryRender = () => {
      if (cancelled) return;
      const api = window.turnstile;
      if (!api) {
        if (Date.now() - startedAt > TIMEOUT_MS) {
          onError?.();
          return;
        }
        setTimeout(tryRender, 100);
        return;
      }
      const id = api.render(container, {
        sitekey: siteKey,
        theme: 'dark',
        callback: (token: string) => onToken(token),
        'expired-callback': () => onExpired?.(),
        'error-callback': () => onError?.(),
      });
      widgetIdRef.current = id;
    };
    tryRender();

    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          // ignore
        }
      }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={ref} className="turnstile-widget" />;
}
